import React, { useEffect, useState } from 'react';
import useFetch from 'use-http';
import Dialog from 'src/components/common/Dialog';
import Panel from 'src/components/common/Panel';
import Button from 'src/components/controls/forms/redesign/FormButton';
import { ODataOptions, ODataOrderByDirection } from 'src/util/ODataBuilder';
import { ITableConfig } from 'src/components/common/table';
import Ap1099 from 'src/models/AP/Ap1099';
import Ap1099Search, { IAp1099SearchCriteria } from '../components/Ap1099Search';
import Ap1099Grid from '../components/Ap1099Grid';
import DialogList from 'src/dialogs';

interface IVendorTaxTypeOption {
    code: string;
    description: string;
}

interface IAp1099MaintenanceDialogProps {
    dialogName: string;
    hideDialog: () => void;
    showDialog: (dialogName: DialogList) => void;
}

const downloadBlobResponseAsFile = (blob: Blob, fileName: string) => {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName || 'document';
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
};

// Ports ap_1099.fmt's Standard/LM_1099/Seed/Export screens into one dialog, per explicit direction
// to collapse the lease side's tabbed "1099 Maintenance" hub into a single search + grid instead of
// separate Find/Scan tabs. Company Maintenance is its own dialog (see CompanyMaintenanceDialog.tsx);
// the TIN Matching File (Oracle's "TIN Test File" - renamed to match what it actually is, the IRS
// TIN Matching Program's bulk-file format) is simple enough (one field - Tax Year) to fold in inline
// next to Seed rather than needing Oracle's separate Export window (which also prompted for a
// filename - not meaningful for a browser download, where the browser itself controls the save
// location).
const Ap1099MaintenanceDialog = (props: IAp1099MaintenanceDialogProps) => {
    const { get, put, post, response } = useFetch();
    const [rows, setRows] = useState<Array<Ap1099>>([]);
    const [taxTypes, setTaxTypes] = useState<Array<IVendorTaxTypeOption>>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [saving, setSaving] = useState<boolean>(false);
    const [seeding, setSeeding] = useState<boolean>(false);
    const [seedYear, setSeedYear] = useState<string>(`${new Date().getFullYear() - 1}`);
    const [tinMatchingYear, setTinMatchingYear] = useState<string>(`${new Date().getFullYear() - 1}`);
    const [generatingTinMatchingFile, setGeneratingTinMatchingFile] = useState<boolean>(false);
    const [message, setMessage] = useState<{ text: string; color?: string } | null>(null);

    // Dialog's own children are mounted/unmounted (CSSTransition unmountOnExit) alongside
    // isOpen, so a plain on-mount fetch here already scopes correctly to "dialog just opened".
    useEffect(() => {
        fetchTaxTypes();
    }, []);

    const fetchTaxTypes = async () => {
        const result = await get('/v1.0/AccountsPayable/vendors/taxtypes');
        if (response.ok && Array.isArray(result)) {
            setTaxTypes(result);
        }
    };

    const fetchAp1099 = async (criteria: IAp1099SearchCriteria) => {
        setLoading(true);
        setMessage(null);

        const tableConfig: ITableConfig = { rowCount: 250, idField: 'AP_ID', pageNumber: 1, columns: [] };
        const options = ODataOptions.from(tableConfig);
        // addOrderBy takes the SELECT list's output alias (it becomes a literal "ORDER BY <name>"
        // appended after the SELECT, so it resolves against the alias).
        options.addOrderBy('TaxYear', ODataOrderByDirection.Descending);

        // Vendor Number and Tax Year are sent as plain query params, NOT OData $filter entries -
        // GetAp1099 takes them as explicit parameters and always ANDs them together. Routing them
        // through $filter hit a real bug: the shared filter-combining logic on the backend decides
        // OR vs AND per filter based on whether that filter's own value happens to be numeric, which
        // silently OR'd these two together (since both are numeric) instead of ANDing them.
        const extraParams: string[] = [];
        if (criteria.vendorNumber) {
            extraParams.push(`vendorNumber=${encodeURIComponent(criteria.vendorNumber)}`);
        }
        if (criteria.taxYear) {
            extraParams.push(`taxYear=${encodeURIComponent(criteria.taxYear)}`);
        }
        const queryString = options.toString() + (extraParams.length > 0 ? (options.toString() ? '&' : '?') + extraParams.join('&') : '');

        const result = await get(`/v1.0/AccountsPayable/ap1099${queryString}`);

        if (response.ok) {
            setRows(result?.items || []);
        } else {
            setMessage({ text: 'Unable to load 1099 records.', color: 'rgba(255,0,0,0.5)' });
        }
        setLoading(false);
    };

    const handleSaveRows = async (updatedRows: Array<Ap1099>) => {
        setSaving(true);
        let anyFailed = false;

        for (const row of updatedRows) {
            await put('/v1.0/AccountsPayable/ap1099', row);
            if (!response.ok) {
                anyFailed = true;
            }
        }

        setSaving(false);
        setMessage(anyFailed
            ? { text: 'Some rows failed to save.', color: 'rgba(255,0,0,0.5)' }
            : { text: 'Changes saved.' });

        if (!anyFailed && rows.length > 0) {
            // Re-query with whatever the current grid already represents (vendor/tax-year scoped
            // implicitly by what's loaded) rather than tracking separate "last search" state.
            const distinctYears = Array.from(new Set(rows.map((r) => r.taxYear)));
            if (distinctYears.length === 1) {
                fetchAp1099({ taxYear: `${distinctYears[0]}` });
            }
        }
    };

    const handleSeed = async () => {
        const taxYear = Number(seedYear);
        if (!taxYear) {
            setMessage({ text: 'Enter a Tax Year to seed.', color: 'rgba(255,0,0,0.5)' });
            return;
        }

        const confirmed = window.confirm(
            `This will delete and rebuild all ${taxYear} 1099 records from A/P voucher history. Continue?`
        );
        if (!confirmed) {
            return;
        }

        setSeeding(true);
        await post(`/v1.0/AccountsPayable/ap1099/seed/${taxYear}`, {});
        setSeeding(false);

        if (response.ok) {
            setMessage({ text: `${taxYear} 1099 records rebuilt.` });
            fetchAp1099({ taxYear: `${taxYear}` });
        } else {
            setMessage({ text: 'Unable to seed 1099 records.', color: 'rgba(255,0,0,0.5)' });
        }
    };

    const handleGenerateTinMatchingFile = async () => {
        const taxYear = Number(tinMatchingYear);
        if (!taxYear) {
            setMessage({ text: 'Enter a Tax Year for the TIN Matching File.', color: 'rgba(255,0,0,0.5)' });
            return;
        }

        setGeneratingTinMatchingFile(true);
        await get(`/v1.0/AccountsPayable/ap1099/tinmatchingfile/${taxYear}`);

        if (response.ok) {
            const blob = await response.blob();
            downloadBlobResponseAsFile(blob, 'TINMatching.txt');
        } else {
            setMessage({ text: 'Unable to generate TIN Matching File.', color: 'rgba(255,0,0,0.5)' });
        }
        setGeneratingTinMatchingFile(false);
    };

    return (
        <Dialog title="1099 Maintenance" dialogName={props.dialogName} bodyStyle={{ width: '90%', maxWidth: '1400px' }}>
            <Ap1099Search onSearch={fetchAp1099} />

            {message && (
                <div style={{ padding: '.5rem', color: message.color ? '#a00' : '#060' }}>{message.text}</div>
            )}

            <Panel backgroundColor="#D3D3D3">
                <Panel panelTitle="1099 Records">
                    <div style={{ padding: '.5rem', maxHeight: '30vh', overflowY: 'auto' }}>
                        {loading ? <div className="bold-centered">Loading...</div> : (
                            <Ap1099Grid rows={rows} taxTypes={taxTypes} onSaveRows={handleSaveRows} saving={saving} />
                        )}
                    </div>
                </Panel>
            </Panel>

            <div className="form-row">
                <Panel backgroundColor="#D3D3D3" style={{ flex: 1, marginRight: '.5rem' }}>
                    <Panel panelTitle="Seed">
                        <div className="form-row" style={{ padding: '.5rem', alignItems: 'center' }}>
                            <label style={{ marginRight: '.5rem' }}>Tax Year</label>
                            <input
                                type="number"
                                value={seedYear}
                                onChange={(e) => setSeedYear(e.currentTarget.value)}
                                style={{ width: '6rem', height: '38px', border: '1px solid #ccc', borderRadius: '4px', padding: '0 .5rem', marginRight: '1rem' }}
                            />
                            <Button
                                text={seeding ? 'Seeding...' : 'Seed'}
                                className="blue-hover-button"
                                disabled={seeding}
                                style={{ width: '8rem' }}
                                onClick={() => handleSeed()}
                            />
                        </div>
                    </Panel>
                </Panel>

                <Panel backgroundColor="#D3D3D3" style={{ flex: 1 }}>
                    <Panel panelTitle="TIN Matching File">
                        <div className="form-row" style={{ padding: '.5rem', alignItems: 'center' }}>
                            <label style={{ marginRight: '.5rem' }}>Tax Year</label>
                            <input
                                type="number"
                                value={tinMatchingYear}
                                onChange={(e) => setTinMatchingYear(e.currentTarget.value)}
                                style={{ width: '6rem', height: '38px', border: '1px solid #ccc', borderRadius: '4px', padding: '0 .5rem', marginRight: '1rem' }}
                            />
                            <Button
                                text={generatingTinMatchingFile ? 'Generating...' : 'Generate'}
                                className="blue-hover-button"
                                disabled={generatingTinMatchingFile}
                                style={{ width: '8rem' }}
                                onClick={() => handleGenerateTinMatchingFile()}
                            />
                        </div>
                    </Panel>
                </Panel>
            </div>

            <div className="form-row" style={{ justifyContent: 'center', marginTop: '1rem' }}>
                <Button
                    text="Reports"
                    className="blue-hover-button"
                    style={{ width: 'auto', paddingLeft: '1rem', paddingRight: '1rem', marginRight: '1rem', whiteSpace: 'nowrap' }}
                    onClick={() => props.showDialog(DialogList.AP_1099_REPORT)}
                />
                <Button
                    text="Company Maintenance"
                    className="blue-hover-button"
                    style={{ width: 'auto', paddingLeft: '1rem', paddingRight: '1rem', marginRight: '1rem', whiteSpace: 'nowrap' }}
                    onClick={() => props.showDialog(DialogList.COMPANY_MAINTENANCE)}
                />
                <Button
                    text="Close"
                    className="blue-hover-button"
                    style={{ width: '8rem' }}
                    onClick={() => props.hideDialog()}
                />
            </div>
        </Dialog>
    );
};

export default Ap1099MaintenanceDialog;



// WEBPACK FOOTER //
// ./src/components/accounts-payable/dialogs/Ap1099MaintenanceDialog.tsx